<template>
  <div>
    <div class="text-right">
   
    </div>

    <div class="flex text-lg mb-4">
      <h1 class="pr-4 border-r uppercase font-bold">
        {{ $route.params.reportOn.replace(/_/g, " ") }}
      </h1>
      <h1 class="px-4 border-r">
        From:
        <span class="font-bold">{{
          $route.params.from | moment("ddd, MMM Do YYYY")
        }}</span>
      </h1>
      <h1 class="px-4">
        To:
        <span class="font-bold">{{
          $route.params.to | moment("ddd, MMM Do YYYY")
        }}</span>
      </h1>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" v-if="data && data.length > 0">
      <!-- Starting Balance -->
      <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Starting Balance</div>
        <div class="text-lg font-bold text-gray-900">GMD {{ formatPrice(summary.startingBalance) }}</div>
        <div class="text-xs text-gray-400">{{ summary.startDate | moment("MMM DD, YYYY HH:mm") }}</div>
      </div>

      <!-- Ending Balance -->
      <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Ending Balance</div>
        <div class="text-lg font-bold text-gray-900">GMD {{ formatPrice(summary.endingBalance) }}</div>
        <div class="text-xs text-gray-400">{{ summary.endDate | moment("MMM DD, YYYY HH:mm") }}</div>
      </div>

      <!-- Total Credited -->
      <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Credited</div>
        <div class="text-lg font-bold text-green-600">+GMD {{ formatPrice(summary.totalCredited) }}</div>
        <div class="text-xs text-gray-400">{{ summary.creditCount }} transaction(s)</div>
      </div>

      <!-- Total Debited -->
      <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Debited</div>
        <div class="text-lg font-bold text-red-600">-GMD {{ formatPrice(summary.totalDebited) }}</div>
        <div class="text-xs text-gray-400">{{ summary.debitCount }} transaction(s)</div>
      </div>
    </div>

    <!-- Net Change Banner -->
    <div v-if="data && data.length > 0" class="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-gray-600">Net Change:</span>
        <div class="text-lg font-bold" :class="summary.netChange >= 0 ? 'text-green-600' : 'text-red-600'">
          {{ summary.netChange >= 0 ? '+' : '' }}GMD {{ formatPrice(summary.netChange) }}
        </div>
      </div>
    </div>
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          <th
            scope="col"
            class="
              px-6
              py-3
              text-left text-xs
              font-medium
              text-gray-500
              uppercase
              tracking-wider
            "
          >
            Nawec Balance
          </th>
          <th
            scope="col"
            class="
              px-6
              py-3
              text-center text-xs
              font-medium
              text-gray-500
              uppercase
              tracking-wider
            "
          >
            Date / Time
          </th>
         
        </tr>
      </thead>

      <tbody class="bg-white">
        <template v-for="(group, groupIndex) in processedGroups">
          <!-- Balance Change Row -->
          <tr
            v-if="group.type === 'change'"
            :key="'change-' + groupIndex"
            :class="getRowClass(group.changeType)"
            class="transition-colors duration-150"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center space-x-3">
                <!-- Change Indicator Icon -->
                <div
                  :class="getIconClass(group.changeType)"
                  class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                >
                  <i :class="getIconType(group.changeType)" class="text-lg"></i>
                </div>
                <!-- Balance Amount -->
                <div>
                  <div class="text-base font-bold" :class="getTextClass(group.changeType)">
                    GMD {{ formatPrice(group.transaction.balance) }}
                  </div>
                  <div v-if="group.changeAmount" class="text-xs font-medium" :class="getTextClass(group.changeType)">
                    {{ group.changeType === 'increase' ? '+' : '' }}{{ group.changeAmount > 0 ? '+' : '' }}GMD {{ formatPrice(Math.abs(group.changeAmount)) }}
                  </div>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <div class="text-sm text-gray-900">
                {{ group.transaction.created_at | moment("MMM DD, YYYY HH:mm:ss") }}
              </div>
            </td>
          </tr>

          <!-- Collapsed Group Header -->
          <tr
            v-else-if="group.type === 'unchanged'"
            :key="'group-' + groupIndex"
            class="bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors duration-200 border-t border-b border-gray-200"
            @click="toggleGroup(groupIndex)"
          >
            <td class="px-6 py-4" colspan="2">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <!-- Collapse Icon -->
                  <div class="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <i
                      :class="isGroupExpanded(groupIndex) ? 'bx bx-chevron-down' : 'bx bx-chevron-right'"
                      class="text-lg text-gray-600 transition-transform duration-200"
                    ></i>
                  </div>
                  <!-- Group Info -->
                  <div>
                    <div class="text-sm font-semibold text-gray-700">
                      {{ group.count }} consecutive records with no balance change
                    </div>
                    <div class="text-xs text-gray-500">
                      Balance: GMD {{ formatPrice(group.balance) }} | 
                      {{ group.firstDate | moment("MMM DD, YYYY HH:mm") }} - 
                      {{ group.lastDate | moment("MMM DD, YYYY HH:mm") }}
                    </div>
                  </div>
                </div>
                <div class="text-xs text-gray-500 font-medium">
                  {{ isGroupExpanded(groupIndex) ? 'Click to collapse' : 'Click to expand' }}
                </div>
              </div>
            </td>
          </tr>

          <!-- Expanded Group Rows -->
          <template v-if="group.type === 'unchanged' && isGroupExpanded(groupIndex)">
            <tr
              v-for="(transaction, idx) in group.transactions"
              :key="'expanded-' + groupIndex + '-' + idx"
              class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150"
            >
              <td class="px-6 py-3 whitespace-nowrap pl-16">
                <div class="flex items-center space-x-2">
                  <div class="w-2 h-2 rounded-full bg-gray-400"></div>
                  <div class="text-sm text-gray-700">
                    GMD {{ formatPrice(transaction.balance) }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-3 whitespace-nowrap text-center">
                <div class="text-xs text-gray-600">
                  {{ transaction.created_at | moment("MMM DD, YYYY HH:mm:ss") }}
                </div>
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>


<script>
export default {
  props: ["data"],

  data: () => ({
    downloadLoading: false,
    filename: "",
    autoWidth: true,
    bookType: "xlsx",
    expandedGroups: {},
  }),

  computed: {
    summary() {
      if (!this.data || this.data.length === 0) {
        return {
          startingBalance: 0,
          endingBalance: 0,
          totalCredited: 0,
          totalDebited: 0,
          creditCount: 0,
          debitCount: 0,
          netChange: 0,
          startDate: null,
          endDate: null,
        };
      }

      const firstRecord = this.data[0];
      const lastRecord = this.data[this.data.length - 1];
      const startingBalance = parseFloat(firstRecord.balance);
      const endingBalance = parseFloat(lastRecord.balance);

      let totalCredited = 0;
      let totalDebited = 0;
      let creditCount = 0;
      let debitCount = 0;
      let previousBalance = startingBalance;

      this.data.forEach((transaction, index) => {
        if (index === 0) return;
        const currentBalance = parseFloat(transaction.balance);
        const change = currentBalance - previousBalance;

        if (change > 0) {
          totalCredited += change;
          creditCount++;
        } else if (change < 0) {
          totalDebited += Math.abs(change);
          debitCount++;
        }
        previousBalance = currentBalance;
      });

      return {
        startingBalance,
        endingBalance,
        totalCredited,
        totalDebited,
        creditCount,
        debitCount,
        netChange: endingBalance - startingBalance,
        startDate: firstRecord.created_at,
        endDate: lastRecord.created_at,
      };
    },

    processedGroups() {
      if (!this.data || this.data.length === 0) return [];

      const groups = [];
      let currentGroup = null;
      let previousBalance = null;

      this.data.forEach((transaction, index) => {
        const currentBalance = parseFloat(transaction.balance);

        if (index === 0) {
          // First record
          groups.push({
            type: 'change',
            changeType: 'initial',
            transaction: transaction,
            changeAmount: null,
          });
          previousBalance = currentBalance;
        } else {
          const balanceChange = currentBalance - previousBalance;

          if (balanceChange === 0) {
            // Balance unchanged
            if (!currentGroup) {
              // Start new unchanged group
              currentGroup = {
                type: 'unchanged',
                balance: transaction.balance,
                count: 1,
                transactions: [transaction],
                firstDate: transaction.created_at,
                lastDate: transaction.created_at,
              };
            } else {
              // Add to existing unchanged group
              currentGroup.count++;
              currentGroup.transactions.push(transaction);
              currentGroup.lastDate = transaction.created_at;
            }
          } else {
            // Balance changed
            if (currentGroup) {
              // Close the unchanged group
              groups.push(currentGroup);
              currentGroup = null;
            }

            // Add change record
            groups.push({
              type: 'change',
              changeType: balanceChange > 0 ? 'increase' : 'decrease',
              transaction: transaction,
              changeAmount: balanceChange,
            });
            previousBalance = currentBalance;
          }
        }
      });

      // Close any remaining unchanged group
      if (currentGroup) {
        groups.push(currentGroup);
      }

      return groups;
    },
  },

  methods: {
    formatPrice(value) {
      if (!value && value !== 0) return '0.00';
      let val = (parseFloat(value) / 1).toFixed(2).replace(",", ".");
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    toggleGroup(groupIndex) {
      const currentState = this.expandedGroups[groupIndex] || false;
      this.$set(this.expandedGroups, groupIndex, !currentState);
    },

    isGroupExpanded(groupIndex) {
      return this.expandedGroups[groupIndex] || false;
    },

    getRowClass(changeType) {
      if (changeType === 'increase') {
        return 'border-l-2 border-green-500 bg-white';
      } else if (changeType === 'decrease') {
        return 'border-l-2 border-red-500 bg-white';
      } else if (changeType === 'initial') {
        return 'border-l-2 border-gray-400 bg-white';
      }
      return 'bg-white';
    },

    getIconClass(changeType) {
      if (changeType === 'increase') {
        return 'bg-green-100 text-green-600';
      } else if (changeType === 'decrease') {
        return 'bg-red-100 text-red-600';
      } else if (changeType === 'initial') {
        return 'bg-gray-100 text-gray-600';
      }
      return 'bg-gray-100 text-gray-500';
    },

    getIconType(changeType) {
      if (changeType === 'increase') {
        return 'bx bx-plus';
      } else if (changeType === 'decrease') {
        return 'bx bx-minus';
      } else if (changeType === 'initial') {
        return 'bx bx-radio-circle-marked';
      }
      return 'bx bx-minus';
    },

    getTextClass(changeType) {
      if (changeType === 'increase') {
        return 'text-green-600';
      } else if (changeType === 'decrease') {
        return 'text-red-600';
      } else if (changeType === 'initial') {
        return 'text-gray-700';
      }
      return 'text-gray-700';
    },

    getFileName() {
      return (
        this.$route.params.reportOn +
        "-" +
        this.$route.params.from +
        "-" +
        this.$route.params.to
      );
    },

    handleDownload() {
      this.downloadLoading = true;
      import("@/vendor/Export2Excel").then((excel) => {
        const tHeader = [
          "Branch",
          "Transactions",
          "Amount",
          "Commission",
          "Total",
        ];
        const filterVal = [
          "branch",
          "transactions",
          "amount",
          "commission",
          "total",
        ];
        const list = this.data;
        console.log("LIST", list);
        const data = this.formatJson(filterVal, list);
        excel.export_json_to_excel({
          header: tHeader,
          data,
          filename: this.getFileName(),
          autoWidth: this.autoWidth,
          bookType: this.bookType,
        });
        this.downloadLoading = false;
      });
    },

    formatJson(filterVal, jsonData) {
      return Object.keys(jsonData).map((v) => {
        console.log();
        return filterVal.map((j) => {
          console.log("J", j);
          console.log();

          if (j === "branch") {
            return v;
          } else if (j === "total") {
            return (
              Number(jsonData[v]["amount"]) + Number(jsonData[v]["commission"])
            );
          } else {
            return jsonData[v][j];
          }
        });
      });
    },
  },
};
</script>